import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * The language popover that opens under the globe icon in the header.
 *
 * The markup is a `<details>` element, so it already opens and closes without
 * JavaScript. This component adds the behaviour the theme's own country/currency
 * dropdown has: closing on an outside click, closing on Escape with focus
 * returned to the trigger, arrow-key navigation between the languages, and a
 * bottom-drawer fallback when the popover would run off a narrow screen.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The disclosure wrapper.
 * @property {HTMLElement} summary - The globe trigger.
 * @property {HTMLDivElement} panel - The popover panel.
 * @property {HTMLButtonElement[]} [options] - One submit button per language.
 *
 * @extends {Component<Refs>}
 */
class LanguagePopoverComponent extends Component {
  requiredRefs = ['details', 'summary', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.details.addEventListener('toggle', this.#onToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.details.removeEventListener('toggle', this.#onToggle);
    this.#unbind();
  }

  get open() {
    return this.refs.details.open;
  }

  /** Closes the popover. */
  close = ({ focusTrigger = false } = {}) => {
    if (!this.open) return;
    this.refs.details.open = false;
    if (focusTrigger) this.refs.summary.focus();
  };

  #onToggle = () => {
    if (this.open) {
      this.#chooseLayout();
      this.#bind();
      // Put the keyboard on the current language so arrow keys start somewhere sensible.
      const options = this.refs.options ?? [];
      const current = options.find((option) => option.getAttribute('aria-current') === 'true');
      (current ?? options[0])?.focus();
    } else {
      this.#unbind();
      this.removeAttribute('data-layout');
    }
  };

  #bind() {
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('resize', this.#onResize);
  }

  #unbind() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('resize', this.#onResize);
  }

  /**
   * Falls back to a bottom drawer when the 200px popover would not fit beside
   * the icon, and back to the popover when it would.
   */
  #chooseLayout() {
    this.removeAttribute('data-layout');

    const { panel } = this.refs;
    const rect = panel.getBoundingClientRect();
    const margin = 8;

    if (rect.left < margin || rect.right > window.innerWidth - margin) {
      this.setAttribute('data-layout', 'sheet');
    }
  }

  #onResize = () => {
    if (this.open) this.#chooseLayout();
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (isClickedOutside(event, this)) this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!this.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close({ focusTrigger: true });
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const options = this.refs.options ?? [];
    if (options.length === 0) return;

    event.preventDefault();

    const index = options.findIndex((option) => option === document.activeElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const next = index === -1 ? 0 : (index + step + options.length) % options.length;

    options[next]?.focus();
  };
}

if (!customElements.get('language-popover-component')) {
  customElements.define('language-popover-component', LanguagePopoverComponent);
}
